import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { GripVertical, Pencil, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

export default function ProposalContentEditor() {
  const [sections, setSections] = useState([]);
  const [editSection, setEditSection] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', body: '' });

  const load = () => api.get('/settings/proposal-sections').then(r => setSections(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const toggleEnabled = async (key, enabled) => {
    await api.put(`/settings/proposal-sections/${key}`, { enabled });
    toast.success(enabled ? 'Section enabled' : 'Section disabled');
    load();
  };

  const openEdit = (s) => {
    setEditSection(s);
    setEditForm({ title: s.title, body: s.body });
  };

  const saveEdit = async () => {
    await api.put(`/settings/proposal-sections/${editSection.key}`, editForm);
    toast.success('Section updated');
    setEditSection(null);
    load();
  };

  const moveSection = async (idx, dir) => {
    const order = sections.map(s => s.key);
    const target = idx + dir;
    if (target < 0 || target >= order.length) return;
    [order[idx], order[target]] = [order[target], order[idx]];
    await api.post('/settings/proposal-sections/reorder', { order });
    load();
  };

  return (
    <div className="space-y-4" data-testid="proposal-content-editor">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-slate-900">Proposal Content Sections</h2>
          <p className="text-sm text-slate-500">Sales content that appears on every customer proposal. Toggle on/off and edit copy.</p>
        </div>
      </div>

      <div className="space-y-2">
        {sections.map((s, idx) => (
          <Card key={s.key} className={`border transition-colors ${s.enabled ? 'border-slate-200' : 'border-dashed border-slate-200 opacity-60'}`}
            data-testid={`section-${s.key}`}>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="flex flex-col gap-1">
                <button onClick={() => moveSection(idx, -1)} disabled={idx === 0}
                  className="text-slate-300 hover:text-slate-500 disabled:opacity-30 transition-colors">
                  <GripVertical className="w-4 h-4 rotate-180" />
                </button>
                <button onClick={() => moveSection(idx, 1)} disabled={idx === sections.length - 1}
                  className="text-slate-300 hover:text-slate-500 disabled:opacity-30 transition-colors">
                  <GripVertical className="w-4 h-4" />
                </button>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-mono text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">{s.key}</span>
                  <h3 className="text-sm font-bold text-slate-800 truncate">{s.title}</h3>
                </div>
                <p className="text-xs text-slate-500 truncate">{s.body?.substring(0, 120)}...</p>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <Switch
                  checked={s.enabled}
                  onCheckedChange={(v) => toggleEnabled(s.key, v)}
                  data-testid={`toggle-${s.key}`}
                />
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(s)}
                  data-testid={`edit-${s.key}`}>
                  <Pencil className="w-3.5 h-3.5 text-slate-500" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Edit Dialog */}
      <Dialog open={!!editSection} onOpenChange={() => setEditSection(null)}>
        <DialogContent className="max-w-lg" data-testid="edit-section-dialog">
          <DialogHeader>
            <DialogTitle>Edit Section: {editSection?.title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Section Title</Label>
              <Input value={editForm.title} onChange={e => setEditForm(p => ({...p, title: e.target.value}))}
                data-testid="section-title-input" />
            </div>
            <div>
              <Label>Content</Label>
              <Textarea value={editForm.body} onChange={e => setEditForm(p => ({...p, body: e.target.value}))}
                rows={8} className="text-sm leading-relaxed" data-testid="section-body-input" />
              <p className="text-[10px] text-slate-400 mt-1">Use line breaks to create paragraphs.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditSection(null)}>Cancel</Button>
            <Button className="bg-sky-500 hover:bg-sky-600 text-white" onClick={saveEdit} data-testid="save-section-btn">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
